import { Command } from 'commander';
import chalk from 'chalk';
import fs from 'fs/promises';
import z from 'zod';
import path from 'path';
import api from '../services/api';
import ux from '../utils/ux';
import {
  messageLinkSchema,
  bookPathSchema,
  bookNameSchema,
  alertDetailsSchema,
} from '../models';
import asyncErrorWrapper from '../utils/async-error-wrapper';
import output from '../utils/output-writer';

const PLAYBOOK_DISPLAY_NAME = 'playbook';

const runPlaybook = new Command(
  `run-${PLAYBOOK_DISPLAY_NAME.replace(' ', '-')}`
)
  .description(`Run ${PLAYBOOK_DISPLAY_NAME} for alert links`)
  .requiredOption(
    '-l, --alert-links <alertLinks...>',
    messageLinkSchema.shape.messageLink.description
  )
  .option('-b, --book-path <bookFilePath>', bookPathSchema.description)
  .option('-n, --book-name <bookName>', bookNameSchema.description)
  .option('-o, --output-path <outputPath>', 'File path to write the output to')
  .option(
    '--verbose',
    'Include thought processes for actions, highlights, and gadget decisions'
  )
  .action(
    asyncErrorWrapper(
      async ({
        alertLinks: links,
        bookPath,
        bookName: name,
        outputPath,
        verbose,
      }: {
        alertLinks: string[];
        bookPath?: string;
        bookName?: string;
        outputPath?: string;
        verbose?: boolean;
      }) => {
        if (bookPath && name) {
          throw new Error(
            'Cannot specify both --book-path and --book-name. Use --book-path to provide a local YAML file, or --book-name to select an existing book.'
          );
        }

        if (!bookPath && !name) {
          console.warn('Book not specified, using book associated with alert');
        }

        const bookYamlStr = bookPath && (await fs.readFile(bookPath, 'utf8'));
        const bookName = name ?? (bookPath && path.basename(bookPath, '.yaml'));

        const result = await ux.runWithProcessingIndicator({
          msg: `Running ${PLAYBOOK_DISPLAY_NAME} for ${chalk.green(links.join(', '))}`,
          func: async () =>
            api.runPlaybooks({
              inputList: links.map((link) => {
                if (link.startsWith('{')) {
                  try {
                    return JSON.parse(link) as z.infer<
                      typeof alertDetailsSchema
                    >;
                  } catch {
                    // Not valid JSON, treat as messageLink
                  }
                }
                return { messageLink: link };
              }),
              bookYamlStr,
              bookName,
              verbose,
            }),
        });

        const resultJson = JSON.stringify(result, null, 2);
        if (outputPath) {
          await fs.writeFile(outputPath, resultJson, 'utf8');
          output.info(`Output written to ${outputPath}`);
        } else {
          output.info(resultJson);
        }
      }
    )
  );

export default runPlaybook;
